import React from 'react';
import Icon from '../../../components/AppIcon';

const TemplateStats = ({ 
  templates = [], 
  className = '' 
}) => {
  const calculateStats = () => {
    const totalTemplates = templates?.length;
    const totalUsage = templates?.reduce((sum, template) => sum + (template?.usageCount || 0), 0);
    const avgUsage = totalTemplates > 0 ? Math.round(totalUsage / totalTemplates) : 0;
    
    const categoryStats = templates?.reduce((acc, template) => {
      acc[template.category] = (acc?.[template?.category] || 0) + 1;
      return acc;
    }, {});

    const mostUsedTemplate = templates?.reduce((max, template) => 
      (template?.usageCount || 0) > (max?.usageCount || 0) ? template : max, 
      templates?.[0] || {}
    );

    const recentlyModified = templates?.sort((a, b) => new Date(b.lastModified) - new Date(a.lastModified))?.slice(0, 3);

    return {
      totalTemplates,
      totalUsage,
      avgUsage,
      categoryStats,
      mostUsedTemplate,
      recentlyModified
    };
  };

  const stats = calculateStats();

  const statCards = [
    {
      title: 'Total Templates',
      value: stats?.totalTemplates,
      icon: 'FileText',
      color: 'text-primary',
      bgColor: 'bg-primary bg-opacity-10'
    },
    {
      title: 'Total Usage',
      value: stats?.totalUsage,
      icon: 'Send',
      color: 'text-success',
      bgColor: 'bg-success bg-opacity-10'
    },
    {
      title: 'Average Usage',
      value: stats?.avgUsage,
      icon: 'BarChart3',
      color: 'text-accent',
      bgColor: 'bg-accent bg-opacity-10'
    },
    {
      title: 'Categories',
      value: Object.keys(stats?.categoryStats)?.length,
      icon: 'Tag',
      color: 'text-secondary',
      bgColor: 'bg-secondary bg-opacity-10'
    }
  ];

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Overview Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards?.map((stat, index) => (
          <div key={index} className="bg-surface border border-border rounded-lg p-4 hover:shadow-card transition-micro">
            <div className="flex items-center space-x-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stat?.bgColor}`}>
                <Icon name={stat?.icon} size={20} className={stat?.color} />
              </div>
              <div>
                <p className="text-2xl font-bold text-text-primary">{stat?.value}</p>
                <p className="text-sm text-text-secondary">{stat?.title}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Category Breakdown */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4">Category Breakdown</h3>
        <div className="space-y-3">
          {Object.entries(stats?.categoryStats)?.map(([category, count]) => {
            const percentage = stats?.totalTemplates > 0 ? Math.round((count / stats?.totalTemplates) * 100) : 0;
            const getCategoryColor = (cat) => {
              switch (cat) {
                case 'birthday': return 'bg-accent';
                case 'follow-up': return 'bg-primary';
                case 'holiday': return 'bg-success';
                case 'custom': return 'bg-secondary';
                default: return 'bg-muted';
              }
            };

            return (
              <div key={category} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 rounded-full ${getCategoryColor(category)}`}></div>
                  <span className="font-medium text-text-primary capitalize">{category}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-24 bg-muted rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${getCategoryColor(category)}`}
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                  <span className="text-sm text-text-secondary w-12 text-right">
                    {count} ({percentage}%)
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Most Used Template */}
      {stats?.mostUsedTemplate?.name && (
        <div className="bg-surface border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-text-primary mb-4">Most Used Template</h3>
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-primary bg-opacity-10 rounded-lg flex items-center justify-center">
              <Icon name="Trophy" size={24} className="text-primary" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-text-primary">{stats?.mostUsedTemplate?.name}</h4>
              <p className="text-text-secondary text-sm mt-1 line-clamp-2">
                {stats?.mostUsedTemplate?.content}
              </p>
              <div className="flex items-center space-x-4 mt-3 text-sm text-text-secondary">
                <div className="flex items-center space-x-1">
                  <Icon name="Send" size={14} />
                  <span>{stats?.mostUsedTemplate?.usageCount || 0} uses</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="Tag" size={14} />
                  <span className="capitalize">{stats?.mostUsedTemplate?.category}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Recently Modified */}
      {stats?.recentlyModified?.length > 0 && (
        <div className="bg-surface border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-text-primary mb-4">Recently Modified</h3>
          <div className="space-y-3">
            {stats?.recentlyModified?.map((template, index) => (
              <div key={template?.id || index} className="flex items-center justify-between py-2">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-muted rounded-lg flex items-center justify-center">
                    <Icon name="FileText" size={16} className="text-text-secondary" />
                  </div>
                  <div>
                    <p className="font-medium text-text-primary">{template?.name}</p>
                    <p className="text-sm text-text-secondary capitalize">{template?.category}</p>
                  </div>
                </div>
                <div className="text-sm text-text-secondary">
                  {new Date(template.lastModified)?.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric'
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateStats;